import { Theme } from "@react-navigation/native";
import { Platform } from "react-native";
import { Merge } from "ts-essentials";

export type LiftColors = Merge<
    {
        body: string;
        primary: string;
        primaryContainer: string;
        secondary: string;
        lightSecondary: string;
        secondaryContainer: string;
        tertiary: string;
        tertiaryContainer: string;
        quaternary: string;
        surface: string;
        surfaceVariant: string;
        background: string;
        error: string;
        errorContainer: string;
        onPrimary: string;
        onPrimaryContainer: string;
        onSecondary: string;
        onSecondaryContainer: string;
        onTertiary: string;
        onTertiaryContainer: string;
        onSurface: string;
        onSurfaceVariant: string;
        onSurfaceDisabled: string;
        onError: string;
        onErrorContainer: string;
        onBackground: string;
        outline: string;
        outlineVariant: string;
        inverseSurface: string;
        inverseOnSurface: string;
        inversePrimary: string;
        shadow: string;
        scrim: string;
        backdrop: string;
        infoBackground: string;
        onInfoBackground: string;
        infoBorder: string;
        warningBackground: string;
        onWarningBackground: string;
        warningBorder: string;
        errorBackground: string;
        onErrorBackground: string;
        errorBorder: string;
        successBackground: string;
        onSuccessBackground: string;
        successBorder: string;
        onNotification: string;
        success: string;
        warning: string;
        fieldLabel: string;
        message: string;
        modalTransparentBackground: string;
        greyAccessible: string;
        accentLighter: string;
        lighterGrey: string;
        darkGrey: string;
        brightGrey: string;
    },
    Theme["colors"]
>;

type Font = {
    fontFamily: string;
    fontWeight:
        | "normal"
        | "bold"
        | "100"
        | "200"
        | "300"
        | "400"
        | "500"
        | "600"
        | "700"
        | "800"
        | "900";
    fontStyle?: "normal" | "italic" | undefined;
};

enum FontsKeys {
    displayLarge = "displayLarge",
    displayMedium = "displayMedium",
    displaySmall = "displaySmall",

    headlineLarge = "headlineLarge",
    headlineMedium = "headlineMedium",
    headlineSmall = "headlineSmall",

    titleLarge = "titleLarge",
    titleMedium = "titleMedium",
    titleSmall = "titleSmall",

    labelLarge = "labelLarge",
    labelMedium = "labelMedium",
    labelSmall = "labelSmall",

    bodyLarge = "bodyLarge",
    bodyMedium = "bodyMedium",
    bodySmall = "bodySmall",

    currencyMedium = "currencyMedium",
    currencyLarge = "currencyLarge",
}

type FontType = {
    fontFamily: string;
    letterSpacing: number;
    fontWeight: Font["fontWeight"];
    lineHeight: number;
    fontSize: number;
    fontStyle?: Font["fontStyle"];
};

export type LiftTheme = {
    gridSize: number;
    dark: boolean;
    marginSize: number;
    roundness: number;
    colors: LiftColors;
    letterSpacing: number;
    fonts: {
        regular: Pick<FontType, "fontFamily" | "fontSize" | "fontWeight">;
        medium: Pick<FontType, "fontFamily" | "fontSize" | "fontWeight">;
        bold: Pick<FontType, "fontFamily" | "fontSize" | "fontWeight">;
        heavy: Pick<FontType, "fontFamily" | "fontSize" | "fontWeight">;
    } & Partial<{
        [key in FontsKeys]: Partial<FontType>;
    }>;
    shadow:
        | {
              shadowOpacity: number;
              shadowColor: string;
              shadowRadius: number;
              shadowOffset: {
                  height: number;
                  width: number;
              };
          }
        | { elevation: number };
    tabBarHeight: number;
};

const GRID_SIZE = 36;

export const theme: LiftTheme = {
    dark: false,

    roundness: 8,
    gridSize: GRID_SIZE,
    marginSize: (2 / 3) * GRID_SIZE, // 2/3 of gridSize
    tabBarHeight: 80,
    fonts: {
        regular: {
            fontFamily: "Inter-Regular",
            fontWeight: "normal",
            fontSize: 16,
        },
        medium: {
            fontFamily: "Inter-Regular",
            fontWeight: "normal",
            fontSize: 16,
        },
        bold: {
            fontFamily: "Inter-Bold",
            fontWeight: "bold",
            fontSize: 16,
        },
        heavy: {
            fontFamily: "Inter-Bold",
            fontWeight: "bold",
            fontSize: 16,
        },

        // Headline
        headlineLarge: {
            fontFamily: "Jubilat-Bold",
            fontSize: 60,
            lineHeight: 80,
        },
        headlineMedium: {
            fontFamily: "Inter-Bold",
            fontSize: 24,
            letterSpacing: -0.48,
            lineHeight: 30,
        },
        headlineSmall: {
            fontFamily: "Inter-Bold",
            fontSize: 22,
        },

        // Title
        titleLarge: {
            fontFamily: "Jubilat-Bold",
            fontSize: 36,
            lineHeight: 43.2,
            letterSpacing: 0.36,
        },
        titleMedium: {
            // Subtitle
            fontFamily: "Jubilat-Bold",
            fontSize: 22,
            lineHeight: 27.5,
            letterSpacing: 0.22,
        },
        titleSmall: {
            // Body Reg2
            fontFamily: "Jubilat-Bold",
            fontSize: 19,
            lineHeight: 26.6,
        },

        // Label
        labelLarge: {
            fontFamily: "Inter-Bold",
            fontSize: 22,
            lineHeight: 28,
        },
        labelMedium: {
            fontFamily: "Inter-Regular",
            fontSize: 17,
            lineHeight: 24,
        },
        labelSmall: {
            fontFamily: "Inter-Regular",
            fontSize: 12,
            lineHeight: 17,
            letterSpacing: 0,
        },

        // Body
        bodyLarge: {
            fontFamily: "Inter-Bold",
            fontSize: 17,
            lineHeight: 24,
            letterSpacing: 0,
        },
        bodyMedium: {
            // Body Reg
            fontFamily: "Inter-Regular",
            fontSize: 17,
            lineHeight: 23.8,
            letterSpacing: -0.34,
        },
        bodySmall: {
            // Footnote Reg
            fontFamily: "Inter-Regular",
            fontSize: 14,
            lineHeight: 20,
            letterSpacing: 0,
        },

        //Currency
        currencyLarge: {
            fontFamily: "SpaceMono-Bold",
            fontSize: 17,
            lineHeight: 24,
            letterSpacing: 0,
        },

        currencyMedium: {
            fontFamily: "SpaceMono-Regular",
            fontSize: 17,
            lineHeight: 24,
            letterSpacing: 0,
        },
    },
    letterSpacing: 1.68,
    colors: {
        background: "#ffffff",
        body: "#000000",
        primary: "#000041",
        onPrimary: "#E9F1F3", // "rgb(252,212,0)"
        secondary: "#0000FF",
        lightSecondary: "#8088ff",
        tertiary: "#000080",
        quaternary: "#F6CFD3",
        infoBackground: "#D0F2FF",
        onInfoBackground: "#13406A",
        infoBorder: "#D0F2FF",
        warningBackground: "#FEF5CC",
        onWarningBackground: "#AF8304",
        warningBorder: "#FEF5CC",
        errorBackground: "#F8D6D5",
        onErrorBackground: "#DE4744", // "rgb(222,71,68)"
        errorBorder: "#F8D6D5",
        successBackground: "#CAF4E1",
        onSuccessBackground: "#1E744B",
        successBorder: "#3CDD73",
        onNotification: "#fff",
        // specific to react-navigation
        card: "white",
        text: "rgb(28, 28, 30)",
        border: "#E9F1F3",
        fieldLabel: "#B1B1B1",
        notification: "rgb(255, 69, 58)",
        backdrop: "rgba(210,210,210, 1.0)",
        success: "#24E086",
        warning: "#FCD400",
        error: "#E75E55",
        message: "#0000FF",
        greyAccessible: "#8c8c8c",
        accentLighter: "#E9F1F3",
        lighterGrey: "#EAEAEA",
        darkGrey: "#3F3F3F",
        brightGrey: "#E8E8E8",
        modalTransparentBackground: "rgba(0, 0, 0, 0.4)",

        // react-native-paper's DefaultTheme values
        primaryContainer: "rgba(234, 221, 255, 1)",
        secondaryContainer: "rgba(232, 222, 248, 1)",
        tertiaryContainer: "rgba(255, 216, 228, 1)",
        surface: "rgba(255, 251, 254, 1)",
        surfaceVariant: "rgba(231, 224, 236, 1)",
        errorContainer: "rgba(249, 222, 220, 1)",
        onPrimaryContainer: "rgba(33, 0, 93, 1)",
        onSecondary: "rgba(255, 255, 255, 1)",
        onSecondaryContainer: "rgba(0, 0, 255, 0.1)",
        onTertiary: "rgba(255, 255, 255, 1)",
        onTertiaryContainer: "rgba(49, 17, 29, 1)",
        onSurface: "rgba(28, 27, 31, 1)",
        onSurfaceVariant: "rgba(73, 69, 79, 1)",
        onSurfaceDisabled: "rgba(28, 27, 31, 0.38)",
        onError: "rgba(255, 255, 255, 1)",
        onErrorContainer: "rgba(65, 14, 11, 1)",
        onBackground: "rgba(28, 27, 31, 1)",
        outline: "rgba(121, 116, 126, 1)",
        outlineVariant: "rgba(202, 196, 208, 1)",
        inverseSurface: "rgba(49, 48, 51, 1)",
        inverseOnSurface: "rgba(244, 239, 244, 1)",
        inversePrimary: "rgba(208, 188, 255, 1)",
        shadow: "rgba(0, 0, 0, 1)",
        scrim: "rgba(0, 0, 0, 1)",
    },
    shadow:
        Platform.OS === "android"
            ? { elevation: 4 }
            : {
                  shadowOpacity: 0.36,
                  shadowColor: "rgb(145, 158, 171)",
                  shadowRadius: 2,
                  shadowOffset: {
                      height: 2,
                      width: 0,
                  },
              },
};
